import React, { useEffect, useRef } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { FieldLoader } from '@atlassian/jira-issue-create-commons/src/common/ui/field-loader/index.tsx';
import { FieldContainer } from '@atlassian/jira-issue-create-commons/src/common/ui/fields/styled.tsx';
import { withFormField } from '@atlassian/jira-issue-create-commons/src/common/ui/with-form-field/index.tsx';
import { closeMenuOnScroll } from '@atlassian/jira-issue-create-commons/src/common/utils/select/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { ExperienceSuccess, useUFOComponentExperience } from '@atlassian/ufo';
import type { ReturnComponentProps } from '@atlassian/jira-issue-create-commons/src/common/ui/with-form-field/types.tsx';
import { LazyEditView } from '../../common/ui/edit-view/main.tsx';
import { entitlementFieldGicMountExperience } from '../../experiences.tsx';
import type { Props, EntitlementFieldConfig, FieldValue } from './types.tsx';
import { validateEntitlementField } from './utils.tsx';

export const EntitlementField = (props: Props): React.JSX.Element => {
	const {
		width,
		autoFocus = false,
		fieldProps: { value, onChange, ...fieldProps },
		onCloseMenuOnScroll = closeMenuOnScroll,
		isDropdownMenuFixedAndLayered = false,
		error,
		contextAndValuesOfIssueBeingCreated,
	} = props;

	const { getFormState } = contextAndValuesOfIssueBeingCreated;
	const formState = getFormState();
	const reporter = formState?.values?.reporter;
	const currentReporterRef = useRef(reporter);

	useEffect(() => {
		if (reporter?.id !== currentReporterRef.current?.id) {
			onChange(null);
			currentReporterRef.current = reporter;
		}
	}, [reporter, currentReporterRef, onChange]);

	return (
		<FieldContainer width={width}>
			<Placeholder name="entitlement-edit" fallback={<FieldLoader />}>
				<ContextualAnalyticsData sourceName="entitlementsFieldGIC" sourceType={MODAL}>
					<FireScreenAnalytics
						attributes={{
							entitlementsFieldViewed: 'GIC',
						}}
					/>
				</ContextualAnalyticsData>
				<LazyEditView
					{...fieldProps}
					isInvalid={Boolean(error)}
					isDropdownMenuFixedAndLayered={isDropdownMenuFixedAndLayered}
					onChange={onChange}
					value={value}
					autoFocus={autoFocus}
					onCloseMenuOnScroll={onCloseMenuOnScroll}
					reporterId={reporter?.id}
					spacing="default"
				/>
				<ExperienceSuccess experience={entitlementFieldGicMountExperience} />
			</Placeholder>
		</FieldContainer>
	);
};

export const EntitlementFieldWithErrorBoundary = (props: Props): React.JSX.Element => {
	useUFOComponentExperience(entitlementFieldGicMountExperience);

	const onError = (_: string, error: Error) => {
		entitlementFieldGicMountExperience.failure();
		throw error; // rethrow error to be caught by WithFormField erorr boundary so that the field label doesn't show
	};

	return (
		<JSErrorBoundary
			id="entitlementField"
			packageName="jiraServicedeskCustomerServiceEntitlementsField"
			onError={onError}
			teamName="boysenberry"
			sendToPrivacyUnsafeSplunk
			fallback="unmount"
		>
			<EntitlementField {...props} />
		</JSErrorBoundary>
	);
};

export const IssueCreateEntitlementField: Flow.AbstractComponent<
	ReturnComponentProps<EntitlementFieldConfig>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	any
> = withFormField<FieldValue, FieldValue>({
	validator: validateEntitlementField,
	// @ts-expect-error - TS2345 - Argument of type '(props: Props) => JSX.Element' is not assignable to parameter of type 'AbstractComponent<ComponentProps<EntitlementFieldConfig, FieldValue>, any>'.
})<EntitlementFieldConfig, FieldValue>(EntitlementFieldWithErrorBoundary);
